/**
 * Query DSL — core `where` condition schema (ERP E1, roadmap #455, PR-2/7).
 *
 * A declarative, transport-safe filter tree that a client (or an AI tool) sends to
 * the server to describe "which rows do I want". This module owns ONLY the SHAPE
 * contract (zod schema + inferred types); it is deliberately storage-agnostic:
 *
 *   - It does NOT know table names, column names, or SQL. The server-side compiler
 *     (`@elmes/api` modules/query/compileDsl) turns a validated tree into a
 *     parameter-bound drizzle condition, resolving every field name through an
 *     entity-derived field-map (the AUTHORITATIVE whitelist). This split keeps the
 *     wire contract reusable (web + AI tools) while the injection-safety and the
 *     tenant base condition live entirely on the server.
 *   - The `field` regex here is a syntactic gate only — a well-formed identifier.
 *     Whether a field is actually queryable (and whether it is a forbidden system
 *     column such as `tenant_id`) is decided fail-closed by the compiler's field-map,
 *     NOT here.
 *
 * The tree is a leaf/composite recursion:
 *   - leaf     `{ field, op, value }` — one comparison against one field.
 *   - composite `{ and: [...] }` / `{ or: [...] }` — boolean combinators.
 * Total nesting depth is capped at {@link MAX_WHERE_DEPTH} (a refine-based depth
 * guard rejects a "depth-bomb" payload before it ever reaches the compiler).
 *
 * PR boundary: this is the core schema + field-map only. The list-tool binding is
 * PR-4; the indexed-JSON generated-column tier is activated in PR-3.
 */
import { z } from 'zod';
/** Comparison operators a leaf may use. */
export const QUERY_OPS = ['eq', 'ne', 'in', 'like', 'range', 'null'];
/**
 * Maximum nesting depth of the `where` tree (leaf = depth 1). Enforced at parse time here
 * (the refine-based depth guard below) AND re-guarded fail-closed in the server compiler
 * (`max_depth_exceeded`), which counts depth in its root pre-pass so an over-deep stored
 * tree is refused before any walk can exhaust the stack.
 */
export const MAX_WHERE_DEPTH = 4;
/**
 * Maximum number of elements allowed in an `in` value array. Enforced at parse time here
 * AND re-guarded fail-closed in the server compiler (`max_in_values_exceeded`): an `in`
 * leaf counts as ONE node at any width, so no other cap bounds it.
 */
export const MAX_IN_VALUES = 100;
/**
 * Maximum number of children allowed in a single `and` / `or` composite. Enforced at
 * parse time here AND re-guarded in the compiler (`max_composite_children_exceeded`),
 * which checks fan-out before the total-node cap so the refusal names this one.
 */
export const MAX_COMPOSITE_CHILDREN = 100;
/**
 * Maximum TOTAL number of nodes (leaves + composites) in a single `where` tree.
 * A COMPLEMENT to {@link MAX_WHERE_DEPTH} + {@link MAX_COMPOSITE_CHILDREN}: those two
 * bound the tree in one dimension each, but a wide-AND-of-wide-ORs payload can stay
 * inside both yet still explode the compiled SQL (e.g. a depth-2 `and` of 100 `or`s of
 * 100 leaves = 10 001 nodes). The total-node cap is the single scalar that bounds the
 * whole compiled predicate size, enforced ONCE at the schema root AND re-guarded fail-
 * closed in the server compiler (`max_nodes_exceeded`).
 */
export const MAX_WHERE_NODES = 100;
/**
 * Syntactic field-name gate. NOT the authority on which fields are queryable —
 * the server compiler's field-map is (unknown / reserved-system field → fail-closed
 * typed error). This regex only bounds the identifier shape so a malformed name can
 * never reach the compiler.
 */
const fieldName = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, 'field must be an identifier ([a-zA-Z_][a-zA-Z0-9_]*)');
/** A scalar comparison value (eq / ne, and each element of an `in` array). */
const scalarValue = z.union([z.string(), z.number(), z.boolean()]);
/** An ordering bound (range gte / lte) — booleans are meaningless for ordering. */
const rangeBound = z.union([z.string(), z.number()]);
const eqLeaf = z
    .object({ field: fieldName, op: z.literal('eq'), value: scalarValue })
    .strict();
const neLeaf = z
    .object({ field: fieldName, op: z.literal('ne'), value: scalarValue })
    .strict();
/**
 * `value` carries `.min(1)` (gh#1753 review round, N7): an empty `like` term compiles to
 * the pattern `%%`, which matches EVERY row — a match-everything that arrives looking like
 * a successful search rather than an error. Rejecting it HERE refuses it at the SCHEMA
 * boundary; compileDsl raises `QueryCompileError('invalid_value', …)` for the same input
 * (callers that build a leaf in-process, bypassing the schema) and the dialect seam throws
 * as a last belt. Measured safe: every in-tree caller already guards with `if (q)`, so no
 * existing request shape starts failing.
 *
 * WHAT THE CALLER OBSERVES (do not write "400" here): this schema is consumed as a TOOL
 * PARAMETER schema by the entity list/aggregate tools, which `safeParse` it and return
 * `{ success: false, error: 'Invalid input: …' }`. No HTTP status is involved on that path.
 * The single surface that maps this class to a real 400 is
 * `api/src/modules/query/queryExport.routes.ts`, and it does so for a where-tree read back
 * out of a SAVED query — not for a live request leaf.
 */
const likeLeaf = z
    .object({ field: fieldName, op: z.literal('like'), value: z.string().min(1) })
    .strict();
const inLeaf = z
    .object({
    field: fieldName,
    op: z.literal('in'),
    value: z.array(scalarValue).min(1).max(MAX_IN_VALUES),
})
    .strict();
const rangeLeaf = z
    .object({
    field: fieldName,
    op: z.literal('range'),
    value: z
        .object({ gte: rangeBound.optional(), lte: rangeBound.optional() })
        .strict()
        .refine((v) => v.gte !== undefined || v.lte !== undefined, {
        message: 'range requires at least one of gte / lte',
    }),
})
    .strict();
const nullLeaf = z
    .object({
    field: fieldName,
    op: z.literal('null'),
    // Optional: true (default) → IS NULL ; false → IS NOT NULL. Value-less is allowed.
    value: z.boolean().optional(),
})
    .strict();
/** A single leaf comparison — a discriminated union keyed by `op`. */
export const queryLeafSchema = z.discriminatedUnion('op', [
    eqLeaf,
    neLeaf,
    likeLeaf,
    inLeaf,
    rangeLeaf,
    nullLeaf,
]);
// Recursive node schema. `z.lazy` breaks the self-reference; each composite branch
// is `.strict()` + non-empty so `{ and: [] }` and stray keys are rejected here.
const queryWhereNode = z.lazy(() => z.union([
    queryLeafSchema,
    z
        .object({
        and: z.array(queryWhereNode).min(1).max(MAX_COMPOSITE_CHILDREN),
    })
        .strict(),
    z
        .object({
        or: z.array(queryWhereNode).min(1).max(MAX_COMPOSITE_CHILDREN),
    })
        .strict(),
]));
/**
 * Depth of a `where` tree: a leaf is depth 1; a composite is 1 + the deepest child.
 * Exported so a caller (and the compiler's defense-in-depth guard) can share one
 * definition of "how deep is this tree".
 */
export function whereDepth(node) {
    if ('and' in node)
        return 1 + Math.max(...node.and.map(whereDepth));
    if ('or' in node)
        return 1 + Math.max(...node.or.map(whereDepth));
    return 1;
}
/**
 * Total node count of a `where` tree: a leaf is 1; a composite is 1 (itself) + the
 * sum of its children's node counts. Exported so the schema root guard and the
 * server compiler's defense-in-depth re-guard share ONE definition of "how big is
 * this tree" (the same reuse pattern as {@link whereDepth} / MAX_WHERE_DEPTH).
 */
export function countWhereNodes(node) {
    if ('and' in node)
        return 1 + node.and.reduce((sum, c) => sum + countWhereNodes(c), 0);
    if ('or' in node)
        return 1 + node.or.reduce((sum, c) => sum + countWhereNodes(c), 0);
    return 1;
}
/**
 * The exported `where` schema: the recursive node PLUS the total-depth cap AND the
 * total-node cap. Both guards are applied ONCE at the root (measured from the root),
 * so a depth-bomb OR a wide fan-out payload is rejected at parse time — long before
 * the compiler. The compiler re-guards both fail-closed (defense in depth).
 */
export const queryWhereSchema = queryWhereNode.superRefine((node, ctx) => {
    const depth = whereDepth(node);
    if (depth > MAX_WHERE_DEPTH) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `where tree depth ${depth} exceeds max ${MAX_WHERE_DEPTH}`,
        });
    }
    const nodes = countWhereNodes(node);
    if (nodes > MAX_WHERE_NODES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `where tree node count ${nodes} exceeds max ${MAX_WHERE_NODES}`,
        });
    }
});
//# sourceMappingURL=dsl.js.map